import type { ComponentPropsWithoutRef, ReactNode } from 'react'
import { useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import type { Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { bridge } from '../../bridge'
import { copyText } from '../../util/clipboard'
import { remarkEmoji } from './emoji'
import { remarkMentions, type MentionResolver } from '../chat/mentions'
import './Markdown.css'

/// Message markdown renderer, shared by chat rooms, the agent transcript, and
/// permission plans. Built on react-markdown + remark-gfm so we get correct
/// nested lists, blockquotes, strikethrough, task lists, tables, and autolinked
/// bare URLs — the full GFM surface — without hand-maintaining a parser.
///
/// This now renders HUMAN-authored text too (not just agent output), so the
/// input is untrusted. Two things keep that safe:
///   1. We never enable rehype-raw, so raw HTML in a message is escaped to text
///      rather than parsed — no markup injection.
///   2. Every link URL is scheme-checked in `MdLink` (see `safeUrl`) before it
///      becomes clickable, and all link taps route through the native
///      `app.openURL` bridge so they open in the system browser, not in-place.
///
/// Remote `![](…)` images are deliberately NOT loaded inline here — a remote
/// image load leaks the viewer's IP to an arbitrary host. Inline images come
/// only from the controlled attachment pipeline; a markdown image degrades to a
/// safe link. See docs on the attachments work.

const REMARK_PLUGINS = [remarkGfm, remarkEmoji]

// Only these schemes may become live links. Everything else (javascript:,
// data:, file:, vbscript:, …) renders as inert text.
const SAFE_SCHEME = /^(https?:|mailto:|tel:)/i
// Does the URL carry an explicit scheme at all? (scheme = letter then
// letters/digits/+/-/. up to the first colon)
const HAS_SCHEME = /^[a-z][a-z0-9+.-]*:/i

// A scheme-less value that looks like a hostname (has a dot, no spaces) — e.g.
// `example.com` or `www.example.com/x`. People routinely write links without
// `https://`, so we upgrade these rather than render them dead.
const HOST_LIKE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9-]+)+(\/\S*)?$/i

/** Return a click-safe URL, or null if the link should be rendered inert.
 *  Absolute URLs must use an allow-listed scheme. A scheme-less value that looks
 *  like a host (`example.com`) is upgraded to https — otherwise a user's
 *  `[docs](example.com)` would silently render as un-clickable text. Anything
 *  else scheme-less (relative paths, the literal `url` placeholder) stays inert. */
export function safeUrl(url: string | undefined | null): string | null {
  if (!url) return null
  const trimmed = url.trim()
  if (!HAS_SCHEME.test(trimmed)) {
    return HOST_LIKE.test(trimmed) ? 'https://' + trimmed : null
  }
  return SAFE_SCHEME.test(trimmed) ? trimmed : null
}

function MdLink({ href, children }: ComponentPropsWithoutRef<'a'>) {
  const url = safeUrl(href)
  // Unsafe or scheme-less: drop the anchor, keep the visible text.
  if (!url) return <>{children}</>
  return (
    <a
      href={url}
      className="md__link"
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => {
        e.preventDefault()
        // Route through the native shell so links open in the system browser
        // instead of navigating the WKWebView away from the app. On web the
        // bridge just does window.open; if it rejects, fall back to that here.
        bridge.call('app.openURL', { url }).catch(() => {
          window.open(url, '_blank', 'noopener,noreferrer')
        })
      }}
    >
      {children}
    </a>
  )
}

function MdImage({ src, alt }: ComponentPropsWithoutRef<'img'>) {
  const url = safeUrl(typeof src === 'string' ? src : undefined)
  const label = alt && alt.trim() ? alt : 'image'
  if (!url) return <>{`🖼 ${label}`}</>
  return <MdLink href={url}>{`🖼 ${label}`}</MdLink>
}

function CodeBlock({ children }: ComponentPropsWithoutRef<'pre'>) {
  const preRef = useRef<HTMLPreElement>(null)
  const [copied, setCopied] = useState(false)
  const copy = () => {
    // Text comes off the rendered node rather than the source string —
    // children are React nodes by the time they reach here.
    const text = preRef.current?.textContent ?? ''
    if (!text) return
    void copyText(text).then((ok) => {
      if (!ok) return
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1200)
    })
  }
  return (
    <div className="md__code-wrap">
      <button type="button" className="md__code-copy" onClick={copy} aria-label="Copy code">
        {copied ? 'Copied' : 'Copy'}
      </button>
      <pre ref={preRef} className="md__code">
        {children}
      </pre>
    </div>
  )
}

function heading(level: 1 | 2 | 3 | 4 | 5 | 6) {
  const Tag = `h${level}` as 'h1'
  // H4–H6 reuse the H3 visual weight; the semantic tag is preserved.
  const styleLevel = Math.min(level, 3)
  return function Heading({ children }: ComponentPropsWithoutRef<typeof Tag>) {
    return <Tag className={`md__h md__h--${styleLevel}`}>{children}</Tag>
  }
}

const COMPONENTS: Components = {
  h1: heading(1),
  h2: heading(2),
  h3: heading(3),
  h4: heading(4),
  h5: heading(5),
  h6: heading(6),
  p: ({ children }) => <p className="md__p">{children}</p>,
  ul: ({ children }) => <ul className="md__ul">{children}</ul>,
  ol: ({ children }) => <ol className="md__ol">{children}</ol>,
  blockquote: ({ children }) => <blockquote className="md__quote">{children}</blockquote>,
  hr: () => <hr className="md__hr" />,
  a: MdLink,
  img: MdImage,
  code: ({ children, className }) => (
    // Inline code only — fenced blocks arrive as <pre><code>, and the <pre>
    // override owns their frame. `.md__code code` neutralizes this inline
    // styling when the code sits inside a block (see Markdown.css).
    <code className={`md__inline-code ${className ?? ''}`.trim()}>{children}</code>
  ),
  pre: CodeBlock,
  table: ({ children }) => (
    <div className="md__table-wrap">
      <table className="md__table">{children}</table>
    </div>
  ),
}

export function Markdown({
  source,
  mentionResolver,
}: {
  source: string
  // When provided (chat surfaces), `<@u:id>`/`<@a:id>` refs render as @name
  // chips resolved to the current display name. Omitted elsewhere → refs stay
  // literal text.
  mentionResolver?: MentionResolver
}): ReactNode {
  const plugins = mentionResolver
    ? [...REMARK_PLUGINS, remarkMentions(mentionResolver)]
    : REMARK_PLUGINS
  return (
    <div className="md">
      <ReactMarkdown
        remarkPlugins={plugins}
        components={COMPONENTS}
        // Identity transform: all URL gating lives in MdLink/safeUrl so it's in
        // one testable place. react-markdown never renders raw HTML without
        // rehype-raw, which we don't use, so this doesn't reopen XSS.
        urlTransform={(url) => url}
      >
        {source}
      </ReactMarkdown>
    </div>
  )
}
